import { useEffect, useState } from 'react'
import { Alert, Button, Card, Checkbox, Space, Typography } from 'antd'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { queryKeys } from '@/shared/api/queryKeys'
import { translateError } from '@/shared/api/errors'
import { PERSON_FIELDS, PROFILE_FIELDS, fetchFormSettings, saveFormSettings } from './formSettingsApi'

/** Abonent formasidagi majburiy maydonlar (ADR-052). */
export function FormSettingsCard() {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const settings = useQuery({ queryKey: queryKeys.subscribers.formSettings, queryFn: ({ signal }) => fetchFormSettings(signal) })
  const [picked, setPicked] = useState<string[]>([])

  useEffect(() => {
    if (settings.data) setPicked(settings.data)
  }, [settings.data])

  const save = useMutation({
    mutationFn: () => saveFormSettings(picked),
    onSuccess: (saved) => queryClient.setQueryData(queryKeys.subscribers.formSettings, saved),
  })
  const changed = settings.data !== undefined && [...picked].sort().join() !== [...settings.data].sort().join()

  const group = (title: string, fields: readonly string[]) => (
    <div>
      <Typography.Text strong>{title}</Typography.Text>
      <Checkbox.Group
        style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 6, marginTop: 8 }}
        value={picked.filter((key) => fields.includes(key))}
        onChange={(values) => setPicked([...picked.filter((key) => !fields.includes(key)), ...values.map(String)])}
        options={fields.map((key) => ({ value: key, label: t(`settings.formFields.field.${key}`) }))}
      />
    </div>
  )

  return (
    <Card title={t('settings.formFields.title')} loading={settings.isPending}>
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Typography.Text type="secondary">{t('settings.formFields.intro')}</Typography.Text>
        {settings.isError || save.isError ? (
          <Alert type="error" showIcon message={translateError(save.error ?? settings.error, t)} />
        ) : null}
        {group(t('settings.formFields.profile'), PROFILE_FIELDS)}
        {group(t('settings.formFields.person'), PERSON_FIELDS)}
        <Button type="primary" loading={save.isPending} disabled={!changed} onClick={() => save.mutate()}>
          {t('common.save')}
        </Button>
      </Space>
    </Card>
  )
}
